/**
 * Google Analytics 4 through Firebase Analytics (`firebase/analytics`) — web and desktop alike — with a regional
 * default (core `logic/analyticsConsent`, shared with iOS and Android):
 *
 * - Where prior opt-in consent is required (EU/EEA, UK, Switzerland, Canada) or the country is unknown, nothing
 *   analytics-related loads, runs or is sent before the person accepts: no gtag script, no cookies, no requests.
 *   Declining (or never answering) keeps it that way.
 * - Elsewhere it is on without a prompt; Settings says "On (you can turn this off)".
 * - Global Privacy Control or Do Not Track turns it off everywhere (unless the person turns it on themselves).
 * - The country comes from GET /v1/auth/methods (`country`, Cloudflare's request country). Until it arrives nothing
 *   runs. An explicit choice (prompt, Settings → Preferences or the profile menu) is stored per browser / desktop
 *   install and always wins.
 * - Google consent mode: everything denied by default; collecting grants `analytics_storage` only (ads storage and ad
 *   personalization stay denied, Google signals off).
 * - No PII and no content: screen names are route types ("channel", "thread"), and every hit's page location/title
 *   is a generic app URL, so workspace slugs, ids and invite tokens never leave the device. Events carry only coarse
 *   parameters (sign-in method, channel kind, thread or not) — never message text, names or emails.
 * - Active in production builds of the real app only (not dev servers, not mock mode); elsewhere it is off and the
 *   consent prompt isn't shown, except mock mode with `?country=` (prompt and settings only, nothing loads).
 * - The signed-in user is known to GA (`user_id`) and Clarity (`identify`) only as an opaque id: the first 32 hex chars
 *   of SHA-256("bundle.telemetry.v1:" + userId) (core logic/telemetry). Never a name or an email.
 * - Errors (uncaught errors, unhandled rejections, API 5xx) become GA4 `exception` events whose description is
 *   scrubbed by core `scrubErrorText` (no emails, URLs, quoted text or ids; 100 chars). No extra vendor.
 * - Microsoft Clarity (session replays, heatmaps; clarity.ts) starts and stops with the same consent.
 */
import { AnalyticsConsent as Rule, TelemetryLogic, type AnalyticsChoice, type AnalyticsDefault, type AnalyticsSignals, type AnalyticsStatus, type KeyValueStorage } from "@bundle/core";
import type { Analytics as FirebaseAnalytics } from "firebase/analytics";
import { clarity } from "./clarity.ts";
import { isDesktop } from "./desktop.ts";
import { MOCK } from "./env.ts";
import { firebaseApp } from "./firebase/app.ts";
import { firebaseOptions } from "./firebase/config.ts";

export type { AnalyticsStatus };

/** The events Bundle records (GA4 recommended names where one fits). */
export type AnalyticsEvent =
  | { name: "sign_up"; params: { method: "email" | "google" | "apple" } }
  | { name: "login"; params: { method: "email" | "google" | "apple" } }
  | { name: "workspace_created"; params?: undefined }
  | { name: "message_sent"; params: { channel_type: "public" | "private" | "dm" | "group_dm" | "unknown"; thread: boolean } }
  | { name: "huddle_started"; params?: undefined }
  | { name: "import_started"; params: { source: ImportSource } }
  | { name: "import_completed"; params: { source: ImportSource } }
  | { name: "mail_sent"; params?: undefined };

/** Where an import comes from: Slack/Notion exports (Settings → Import) or mail (IMAP, Google Workspace). */
export type ImportSource = "slack" | "notion" | "googleDrive" | "imap" | "google";

/** The opaque telemetry id for a user (core logic/telemetry): SHA-256 via WebCrypto. */
export async function telemetryId(userId: string): Promise<string> {
  const bytes = new TextEncoder().encode(TelemetryLogic.telemetryIdSource(userId));
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return TelemetryLogic.telemetryIdFromDigest(Array.from(digest, (b) => b.toString(16).padStart(2, "0")).join(""));
}

const CONSENT_KEY = "pref.analytics";
/** GA can load: production build of the real app with a measurement id. */
const REAL = import.meta.env.PROD && !MOCK && firebaseOptions.measurementId !== undefined;
/**
 * Mock mode can force a country (`?country=DE`, sticky for the tab; `?gpc` adds Global Privacy Control) to check the
 * prompt and the settings without loading anything. The mock API answers that country from /v1/auth/methods.
 */
const MOCK_FORCED = MOCK && mockParam("country") !== undefined;
const AVAILABLE = REAL || MOCK_FORCED;

function mockParam(name: "country" | "gpc"): string | undefined {
  try {
    const params = new URLSearchParams(location.search);
    if (params.has(name)) sessionStorage.setItem(`bundle.mock.${name}`, params.get(name) || "1");
    return sessionStorage.getItem(`bundle.mock.${name}`) ?? undefined;
  } catch {
    return undefined;
  }
}

/** The browser's privacy signals (GPC; DNT "1"). */
function browserSignals(): AnalyticsSignals {
  if (typeof navigator === "undefined") return {};
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return {
    globalPrivacyControl: nav.globalPrivacyControl === true || (MOCK_FORCED && mockParam("gpc") !== undefined),
    doNotTrack: nav.doNotTrack === "1",
  };
}
/** Every hit claims this location (no ids, slugs or tokens from the real URL). */
const GENERIC_PAGE = (screen: string) => `${location.origin}/${screen}`;

export class AnalyticsService {
  private readonly storage: KeyValueStorage;
  private readonly listeners = new Set<() => void>();
  private choice: AnalyticsChoice | undefined;
  private readonly signals = browserSignals();
  /** Undefined until the country is known (nothing runs and nothing is asked meanwhile). */
  private regional: AnalyticsDefault | undefined;
  private status: AnalyticsStatus;
  private instance: Promise<FirebaseAnalytics | undefined> | undefined;
  private lastScreen: string | undefined;
  /** The signed-in user's opaque id (undefined: signed out). */
  private userId: string | undefined;

  constructor(storage: KeyValueStorage) {
    this.storage = storage;
    const saved = storage.get(CONSENT_KEY);
    this.choice = saved === "granted" || saved === "denied" ? saved : undefined;
    // A privacy signal decides without the country.
    if (Rule.analyticsDefault(null, this.signals) === "off") this.regional = "off";
    this.status = this.compute();
    if (this.status.collecting) void this.load();
    if (REAL && this.status.collecting) clarity.setCollecting(true, undefined);
    if (REAL) installErrorHandlers(this);
  }

  /** The signed-in user (undefined: signed out): only its opaque hash reaches GA and Clarity. */
  identify(userId: string | undefined): void {
    void (userId === undefined ? Promise.resolve(undefined) : telemetryId(userId).catch(() => undefined)).then((id) => {
      if (id === this.userId) return;
      this.userId = id;
      clarity.identify(id);
      void this.withInstance((ga, instance) => ga.setUserId(instance, id ?? null));
    });
  }

  /** A GA4 `exception` event (scrubbed description; no stack, no content). */
  exception(error: unknown, fatal = false): void {
    const text = error instanceof Error ? `${error.name}: ${error.message}` : typeof error === "string" ? error : "error";
    void this.withInstance((ga, instance) => {
      ga.logEvent(instance, "exception", { description: TelemetryLogic.scrubErrorText(text), fatal, page_location: GENERIC_PAGE(this.lastScreen ?? "app"), page_title: "Bundle" });
    });
  }

  /** The regional default needs the country: no explicit choice and no privacy signal yet. */
  get needsCountry(): boolean {
    return AVAILABLE && this.choice === undefined && this.regional === undefined;
  }

  /** The request country from GET /v1/auth/methods (undefined: unknown or the request failed → ask). */
  setCountry(country: string | undefined): void {
    if (this.regional !== undefined) return;
    this.regional = Rule.analyticsDefault(country, this.signals);
    this.changed();
  }

  private compute(): AnalyticsStatus {
    return Rule.analyticsStatus(this.choice, this.regional ?? "off");
  }

  private changed(): void {
    const was = this.status.collecting;
    this.status = this.compute();
    for (const l of Array.from(this.listeners)) l();
    if (this.status.collecting && !was) {
      void this.load().then(() => {
        if (this.lastScreen !== undefined) this.screen(this.lastScreen);
      });
    } else if (!this.status.collecting && was) this.unload();
    if (this.status.collecting !== was) clarity.setCollecting(REAL && this.status.collecting, this.userId);
  }

  /** Analytics can run in this build (production, real API). */
  readonly available = AVAILABLE;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** `collecting`, `prompt` (show the question) and `setting` (how Settings reads). Stable between changes. */
  getSnapshot = (): AnalyticsStatus => this.status;

  /** The person allowed or declined (consent prompt, Settings, profile menu): stored, and it always wins. */
  setConsent(granted: boolean): void {
    const next = granted ? "granted" : "denied";
    if (next === this.choice) return;
    this.choice = next;
    this.storage.set(CONSENT_KEY, next);
    this.changed();
  }

  private unload(): void {
    if (this.instance !== undefined) {
      // Withdrawn mid-session: stop collecting now and drop the cookies (nothing loads again after a reload).
      void Promise.all([import("firebase/analytics"), this.instance]).then(([ga, instance]) => {
        ga.setConsent({ analytics_storage: "denied" });
        if (instance !== undefined) ga.setAnalyticsCollectionEnabled(instance, false);
        clearAnalyticsCookies();
      });
      this.instance = undefined;
    }
  }

  /** A screen became visible (router): `screen` is a route type, never a URL. */
  screen(screen: string): void {
    this.lastScreen = screen;
    void this.withInstance((ga, instance) => {
      ga.logEvent(instance, "screen_view", {
        firebase_screen: screen,
        firebase_screen_class: screen,
        page_location: GENERIC_PAGE(screen),
        page_title: "Bundle",
      });
    });
  }

  track(event: AnalyticsEvent): void {
    // Plain string: the SDK's per-name overloads don't take a union of recommended and custom names.
    const name: string = event.name;
    void this.withInstance((ga, instance) => {
      ga.logEvent(instance, name, { ...event.params, page_location: GENERIC_PAGE(this.lastScreen ?? "app"), page_title: "Bundle" });
    });
    // Replays can be filtered by these (name only, no parameters).
    if (this.status.collecting) clarity.event(name);
  }

  private async withInstance(fn: (ga: typeof import("firebase/analytics"), instance: FirebaseAnalytics) => void): Promise<void> {
    if (!this.status.collecting || this.instance === undefined) return;
    try {
      const [ga, instance] = await Promise.all([import("firebase/analytics"), this.instance]);
      if (instance !== undefined && this.status.collecting) fn(ga, instance);
    } catch {
      // Blocked by an extension or offline: analytics is best effort.
    }
  }

  private load(): Promise<FirebaseAnalytics | undefined> {
    if (!REAL || !this.status.collecting) return Promise.resolve(undefined);
    this.instance ??= (async () => {
      try {
        const ga = await import("firebase/analytics");
        if (!(await ga.isSupported())) return undefined;
        // Consent mode: the default is everything denied; analytics storage only is granted (chosen, or the regional default).
        ga.setConsent({ ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
        const instance = ga.initializeAnalytics(await firebaseApp(), {
          config: {
            send_page_view: false,
            page_location: GENERIC_PAGE("app"),
            page_title: "Bundle",
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
          },
        });
        // Initialization pushes that default once the SDK's config fetch is done; the update must come after it.
        // getGoogleAnalyticsClientId waits for exactly that (bounded: a blocked gtag.js never answers).
        await Promise.race([ga.getGoogleAnalyticsClientId(instance).catch(() => undefined), new Promise((r) => setTimeout(r, 10_000))]);
        ga.setConsent({ analytics_storage: "granted" });
        ga.setDefaultEventParameters({ app_platform: isDesktop() ? "desktop" : "web" });
        if (this.userId !== undefined) ga.setUserId(instance, this.userId);
        return instance;
      } catch {
        return undefined;
      }
    })();
    return this.instance;
  }
}

let errorHandlersInstalled = false;
/** Uncaught errors and unhandled rejections → `exception` (sent only while collecting). */
function installErrorHandlers(analytics: AnalyticsService): void {
  if (errorHandlersInstalled || typeof window === "undefined") return;
  errorHandlersInstalled = true;
  window.addEventListener("error", (e) => {
    // Resource load errors (img/script) have no `error`; a cross-origin "Script error." says nothing.
    if (e.error !== undefined && e.error !== null) analytics.exception(e.error, true);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason: unknown = e.reason;
    // Offline and cancelled requests are expected, not errors.
    if (reason instanceof Error && (reason.name === "AbortError" || (reason as { kind?: string }).kind === "network")) return;
    analytics.exception(reason, false);
  });
}

/** Expires gtag's first-party cookies (`_ga`, `_ga_<id>`) on this host and its parent domains. */
function clearAnalyticsCookies(): void {
  const names = document.cookie
    .split(";")
    .map((c) => c.split("=")[0]!.trim())
    .filter((n) => n === "_ga" || n.startsWith("_ga_"));
  const parts = location.hostname.split(".");
  const domains = ["", ...parts.slice(0, -1).map((_, i) => `; domain=.${parts.slice(i).join(".")}`)];
  for (const name of names) for (const d of domains) document.cookie = `${name}=; Max-Age=0; path=/${d}`;
}
